import { getBuyRequestMeta } from '../../helpers/buy-request'
import { CustomerTrack, getCustomerTrackData } from '../../helpers/track-config'
import { deepEqual, getCookie } from '../Utils'
import type { IRogue } from '../RogueTypes'
import type { AnalyticsEvent, HasItems, IRogueAnalytics } from '../../../../types/Analytics'
import type { AnalyticsConsumer } from './consumers/analytics-consumer'

function hasItems(obj: any): obj is HasItems {
  return obj.items !== undefined
}

// A shared event_id is stamped on every event before consumers run.
// FBConsumer sends it as the fbq eventID; RudderstackConsumer forwards it to CAPI.
// Meta uses matching IDs to deduplicate browser pixel + CAPI events for the same action.
function generateFbEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0

    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

export class Analytics implements IRogueAnalytics {
  rogue: IRogue | undefined

  consumers: AnalyticsConsumer[] = []

  initialized = false

  private pendingImpressions: (AnalyticsEvent & {type: 'listView'})[] = []

  private pendingEvents: AnalyticsEvent[] = []

  private customerData: CustomerTrack | undefined

  private sendListImpressionTimeout: number = 0

  getCookiePreferences(rogue: IRogue) {

    const retval = {
      performance : rogue.site.cookieSettings?.performance ?? true as boolean,
      marketing   : rogue.site.cookieSettings?.marketing ?? true as boolean,
      functional  : rogue.site.cookieSettings?.functional ?? true as boolean,
    }

    // Read from the cookie
    const cookie = getCookie('cookiePreferences')

    if (!cookie) {
      if (['EUR', 'GBR', 'CAN'].includes(rogue.site.code)) {
        // Should not get here - we dont want to call init until we have a cookie value
        rogue.log.warn('Called track.init before setting a cookie for cookiePreferences')
      }
    } else {
      try {
        const preferences = JSON.parse(cookie) as {marketing: boolean, functional: boolean, performance: boolean}
        retval.marketing = preferences.marketing
        retval.functional = preferences.functional
        retval.performance = preferences.performance
      } catch (e) {
        // Could not read cookie - setting marketing and analytics to false...
        rogue.log.warn('Could not read cookie preferences cookie')
      }
    }

    return retval
  }

  async init(rogue: IRogue): Promise<void> {
    // We will use this to stop marketing services from starting, and preventing the marketing cookies, on users that have GPC enabled.
    // @ts-expect-error: globalPrivacyControl is not yet a standard part of the DOM
    const gpcValue = !!window.navigator?.globalPrivacyControl

    rogue.log.debug('Loading analytics')
    this.rogue = rogue

    if (rogue.ssr) {
      return
    }

    const { bing, bv, fb, ga4, rs, ads, ads30days, pap, iterableClient, openai }  = rogue.keys

    const services:{[key: string] : boolean} = {
      bing       : !!bing,
      bv         : !!bv,
      fb         : !!fb,
      ga4        : !!ga4,
      rs         : !!rs,
      gads       : !!ads,
      gads30days : !!ads30days,
      pap        : !!pap,
      iterable   : !!iterableClient,
      openai     : !!openai,
    }

    rogue.log.info('Analytics Init: ', JSON.stringify(services))

    const { performance, marketing, functional } = this.getCookiePreferences(rogue)

    // Lazy-load all analytics consumer modules in parallel
    const [
      { GA4Consumer },
      { GAdsConsumer },
      { RudderstackConsumer },
      { FBConsumer },
      { BazaarvoiceConsumer },
      { BingConsumer },
      { PostAffiliateProConsumer },
      { IterableConsumer },
      { RFKConsumer },
      { ClarityConsumer },
      { OpenAIConsumer },
    ] = await Promise.all([
      import('./consumers/ga4'),
      import('./consumers/gads'),
      import('./consumers/rs'),
      import('./consumers/meta'),
      import('./consumers/bazaarvoice'),
      import('./consumers/bing'),
      import('./consumers/post-affiliate-pro'),
      import('./consumers/iterable'),
      import('./consumers/rfk'),
      import('./consumers/clarity'),
      import('./consumers/openai'),
    ])

    // We always start GA, since google provides a way to set tracking preferences
    if (ga4) {
      let ga4Consumer = this.consumers.find(c => c.name === 'ga4') as InstanceType<typeof GA4Consumer>

      if (!ga4Consumer) {
        ga4Consumer = new GA4Consumer(rogue, ga4)
        this.consumers.push(ga4Consumer)
      }

      ga4Consumer.setPermissions(marketing, performance, functional)

    }

    // Since ads uses gtag, and gtag has a setPermissions function, we can load this always
    if (ads && !this.consumers.some(c => c.name === 'ads')) {
      this.consumers.push(new GAdsConsumer(rogue, ads))
    }

    if (ads30days && !this.consumers.some(c => c.name === 'ads30days')) {
      this.consumers.push(new GAdsConsumer(rogue, ads30days, 'ads30days'))
    }

    if (rs) {
      let rsConsumer = this.consumers.find(c => c.name === 'Rudderstack') as InstanceType<typeof RudderstackConsumer>
      if (!rsConsumer) {
        // rudderanalytics may not have started yet
        if (!window.rudderanalytics ) {
          (window as any).startRudder()
        }
        rsConsumer = new RudderstackConsumer(rogue)
        this.consumers.push(rsConsumer)
      }

      // Populate the consent IDs based on the user's choice and send them to the RS consumer.
      rsConsumer.setPermissions(marketing, performance, functional)
    }


    if (fb && marketing && !gpcValue && !this.consumers.some(c => c.name === 'facebook')) {
      // If this is EU, we may not have loaded the FB script yet
      if (!(window as any).fbq) {
        (window as any).startFb()
      }
      this.consumers.push(new FBConsumer(rogue, fb))
    }

    if (openai && marketing && !gpcValue && !this.consumers.some(c => c.name === 'openai')) {
      if (!(window as any).oaiq) {
        (window as any).startOpenAI()
      }
      this.consumers.push(new OpenAIConsumer(rogue, openai))
    }

    if (rogue.keys.clarity && marketing && !gpcValue && !this.consumers.some(c => c.name === 'clarity')) {
      this.consumers.push(new ClarityConsumer(rogue, rogue.keys.clarity))
    }

    // Essential
    if (bv && !this.consumers.some(c => c.name === 'bazaarvoice')) {
      this.consumers.push(new BazaarvoiceConsumer(rogue, bv))
    }

    if (bing && marketing && !gpcValue && !this.consumers.some(c => c.name === 'bing')) {
      // Bing doesnt load its script until it is added as a consumer
      this.consumers.push(new BingConsumer(rogue, bing))
    }

    // Essential
    if (pap && !this.consumers.some(c => c.name === 'PAP')) {
      this.consumers.push(new PostAffiliateProConsumer(rogue))
    }

    if (iterableClient && marketing && !gpcValue && !this.consumers.some(c => c.name === 'iterable')) {
      this.consumers.push(new IterableConsumer(rogue))
    }

    // Essential
    if (!this.consumers.some(c => c.name === 'rfk')) {
      this.consumers.push(new RFKConsumer(rogue))
    }
    this.initialized = true

    // If this happens after initial page load, we need to send the queued up tracking events
    if (this.pendingEvents.length) {
      this.rogue.log.info(`Analytics initialized. Sending ${this.pendingEvents.length} pending events.`)
      this.pendingEvents.forEach(e => this.track(e))
      this.pendingEvents = []
    }
  }

  identify() {
    const rogue = this.rogue
    if (!rogue || rogue.ssr) {
      // Dont save any events for SSR
      return
    }
    const data = getCustomerTrackData(rogue)

    // Dont update if the customer is still loading, or equivalent to the existing data
    if (data?.loading || deepEqual(data, this.customerData)) {
      return
    }

    this.customerData = data

    // Update the feature flag settings for this customer
    rogue.featureFlags.updateAttributes()

    // Call Identify for different trackers
    this.consumers.forEach(consumer => {
      if (!consumer.identify) {
        return
      }

      try {
        consumer.identify(data)
      } catch (e) {
        this.rogue?.log.info(`Error identifying for ${consumer.name}`, e as Error)
      }
    })
  }

  /**
   * Debounce multiple list item views
   * This allows us to easily only send list item views as an item becomes visible, instead of the entire list at once
   * @param event
   */
  listItemViewDebounce(event: AnalyticsEvent & {type: 'listView'}) {
    clearTimeout(this.sendListImpressionTimeout)
    const existingListImpression = this.pendingImpressions.find(impr => impr.listType === event.listType && impr.listEntity?.id === event.listEntity?.id)

    if (existingListImpression) {
      existingListImpression.rfkProducts.push(...event.rfkProducts)
    } else {
      this.pendingImpressions.push(event)
    }

    this.sendListImpressionTimeout = window.setTimeout(() => this.sendListImpressions(), 1000)
  }

  sendListImpressions() {
    if (this.pendingImpressions.length) {
      this.pendingImpressions.forEach(event => this.track(event))
      this.pendingImpressions = []
    }
  }

  track(event: AnalyticsEvent) {
    const rogue = this.rogue
    if (!rogue || rogue.ssr) {
      // Dont save any events for SSR
      return
    }

    if (!this.initialized) {
      this.rogue?.log.info('Not yet initialized. Pushing event to queue.')
      this.pendingEvents.push(event)

      return
    }

    if (event.type === 'listView' || event.type === 'productClick') {
      // Create a unique listId to identify what is shown / clicked
      const { type, sku, id, name, url } = event.listEntity || {}
      const listEntityId = sku || id || url
      event.listId = `${type || 'widget'}${(listEntityId) ? `-${listEntityId}` : ''}-${event.listType}`
      event.listName = name ? `${type} ${name} ${listEntityId}: ${event.listType}` : `${event.listType}`
    }

    if (event.type === 'login') {
      event.data = getCustomerTrackData(rogue)
      event.userId = event.data._id
    }

    ;(event as any).fbEventId = generateFbEventId()

    try {
      if (hasItems(event) && !event.itemMetas) {
        // Add buy request metas to the event, since these are more useful than just normal items
        try {
          event.itemMetas = event.items?.map(buyRequest => {
            if (buyRequest.type === 'group') {
              // MOST of the time we care about the single child with quantity...
              const withQty = buyRequest.children?.filter(c => c.qty > 0)
              if (withQty?.length === 1) {
                return getBuyRequestMeta(withQty[0], rogue, buyRequest)
              }
            }

            return getBuyRequestMeta(buyRequest, rogue)
          })
        } catch (e) {
          rogue.log.warn('Unable to process item into buyRequestMeta', { error: e })
        }
      }

      this.consumers.forEach(consumer => {
        try {
          consumer.track?.(event)
        } catch (e) {
          this.rogue?.log.info(`Error consuming ${event.type} for ${consumer.name}`, e as Error)
        }
      })
    } catch (e) {
      rogue.log.warn(`Uncaught Error with tracking call ${event.type}`, e as Error)
    }
  }
}
